import type { DiagnosisMapResponse, MedicationExtractionFailureKind } from "@taskhealth/shared";
import Constants from "expo-constants";

import { notifyIfAccountInactive } from "@/lib/account-status";
import { getApiBase } from "@/lib/api-env";
import { getAccessToken, refreshAccessToken } from "@/lib/auth";
import type { CaptureQuality } from "@/lib/capture-location";
import { captureSentryException } from "@/lib/sentry-init";

const APP_VERSION = Constants.expoConfig?.version ?? "unknown";

const TOKEN_REFRESH_BUFFER_S = 300; // Refresh 5 minutes before expiry

// ─── Typed errors ──────────────────────────────────────
// Callers need to distinguish network failures (retryable) from HTTP errors
// (server rejected) from aborts (user cancelled). Throwing plain `Error(msg)`
// collapses all three into the same string and forces brittle regex parsing
// downstream. ApiError carries a kind discriminator + HTTP status code so
// retry / logout / "already done" decisions can be made cleanly via
// instanceof + ApiError.kind.
export type ApiErrorKind = "network" | "abort" | "http";

export class ApiError extends Error {
    readonly kind: ApiErrorKind;
    readonly status: number | undefined;
    /**
     * The parsed `error` envelope from the server response (when the body
     * was JSON-parsable). Lets specific call sites read structured payload
     * like `{ code, message, blockers, asOfDate }` without re-parsing.
     * Undefined for network/abort errors.
     */
    readonly data: Record<string, unknown> | undefined;
    constructor(
        message: string,
        kind: ApiErrorKind,
        status?: number,
        data?: Record<string, unknown>
    ) {
        super(message);
        this.name = "ApiError";
        this.kind = kind;
        this.status = status;
        this.data = data;
    }
}

function asApiErrorFromCatch(e: unknown, signal?: AbortSignal): ApiError {
    // The request's own signal decides first: since SDK 56 `fetch` is expo/fetch,
    // whose cancellation is a plain Error "Fetch request has been canceled"
    // (no AbortError name, no "aborted"), so the text checks alone would call a
    // cancelled request a network failure.
    if (
        signal?.aborted ||
        (e instanceof Error &&
            (e.name === "AbortError" || /aborted|canceled|cancelled/i.test(e.message)))
    ) {
        return new ApiError("Request was cancelled", "abort");
    }
    return new ApiError("Network error — check your connection", "network");
}

/** Decode JWT payload without verification (just reads exp) */
function isTokenExpiringSoon(token: string): boolean {
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const exp = payload.exp as number;
        return Date.now() / 1000 > exp - TOKEN_REFRESH_BUFFER_S;
    } catch {
        return true; // Can't decode → treat as expired
    }
}

/** Get a valid (non-expired) access token, refreshing proactively if needed */
export async function getValidToken(): Promise<string | null> {
    const token = getAccessToken();
    if (!token) {
        return null;
    }
    if (isTokenExpiringSoon(token)) {
        console.warn("[Auth] Token expiring soon, refreshing...");
        const newToken = await refreshAccessToken();
        console.warn("[Auth] Token refreshed:", newToken ? "success" : "failed");
        return newToken;
    }
    return token;
}

// Default per-request wall-clock ceiling. Without it, a mobile fetch sits on
// the network for however long the ALB / CDN allows, and the section screen's
// 12-retry path exhausts the API's connection pool.
const DEFAULT_REQUEST_TIMEOUT_MS = 120_000;

// ─── Visit Recording event hook ─────────────────────────────────────
// Lazy-imported to break the api.ts ↔ recorder module-load cycle. Never throws
// — if the recorder isn't started, the call is a no-op.
async function emitRecorderEvent(
    eventType: string,
    payload: Record<string, unknown>,
    severity: "info" | "warn" | "error" = "info"
): Promise<void> {
    try {
        const mod = await import("@/lib/recorder");
        mod.Recorder.addEvent(eventType, payload, severity);
    } catch {
        // recorder not loaded yet, or module failed to import — silent
    }
}

// PHI-sensitive routes — capture only the path/method/status/duration. Bodies
// stay out of the event log; they're already in our DB and not needed for
// the player. This list is intentionally tight and conservative.
function isPhiSafePath(path: string): boolean {
    if (path.includes("/answers")) {
        return false;
    }
    if (path.includes("/medications")) {
        return false;
    }
    if (path.includes("/diagnoses")) {
        return false;
    }
    if (path.includes("/teaching")) {
        return false;
    }
    if (path.includes("/notes")) {
        return false;
    }
    if (path.includes("/patient")) {
        return false;
    }
    // Patient context: the intro, hints and source items are all about the patient.
    if (path.includes("/context")) {
        return false;
    }
    return true;
}

/**
 * AbortSignal that fires after `ms`. Polyfills AbortSignal.timeout for
 * runtimes that don't ship it (older Hermes throws TypeError: undefined is
 * not a function for AbortSignal.timeout, which silently breaks every fetch).
 */
function timeoutSignal(ms: number): AbortSignal {
    type Static = { timeout?: (ms: number) => AbortSignal };
    const fn = (AbortSignal as unknown as Static).timeout;
    if (typeof fn === "function") {
        return fn(ms);
    }
    const ctrl = new AbortController();
    setTimeout(() => ctrl.abort(), ms);
    return ctrl.signal;
}

/**
 * Compose two AbortSignals into one that aborts when either fires.
 * Uses AbortSignal.any when available (modern Hermes) and falls back to a
 * manual composite otherwise.
 */
function composeSignals(a: AbortSignal | undefined, b: AbortSignal): AbortSignal {
    if (!a) {
        return b;
    }
    type SignalCtor = { any?: (signals: AbortSignal[]) => AbortSignal };
    const ctor = (AbortSignal as unknown as SignalCtor).any;
    if (typeof ctor === "function") {
        return ctor([a, b]);
    }
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    if (a.aborted) {
        controller.abort();
    } else {
        a.addEventListener("abort", onAbort, { once: true });
    }
    if (b.aborted) {
        controller.abort();
    } else {
        b.addEventListener("abort", onAbort, { once: true });
    }
    return controller.signal;
}

export async function request<T>(
    path: string,
    options?: RequestInit & { signal?: AbortSignal; timeoutMs?: number }
): Promise<T> {
    const token = await getValidToken();

    const headers: Record<string, string> = {
        ...(options?.body ? { "Content-Type": "application/json" } : {}),
        "X-App-Version": APP_VERSION,
        ...(options?.headers as Record<string, string>)
    };
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const timeoutMs = options?.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;

    function buildSignal(): AbortSignal {
        return composeSignals(options?.signal, timeoutSignal(timeoutMs));
    }

    // Visit Recording: emit network event (non-blocking, lazy import to break
    // the api.ts ↔ recorder cycle at module-load time).
    const method = options?.method ?? "GET";
    const requestBodyBytes = typeof options?.body === "string" ? options.body.length : undefined;
    const startedAt = Date.now();
    void emitRecorderEvent("network_request", {
        path,
        method,
        requestBodyBytes,
        // Never log auth headers; allow-list non-sensitive routes.
        bodySafe: isPhiSafePath(path)
    });

    let res: Response;
    const signal = buildSignal();
    try {
        res = await fetch(`${getApiBase()}${path}`, {
            ...options,
            headers,
            signal
        });
    } catch (e) {
        throw asApiErrorFromCatch(e, signal);
    }
    void emitRecorderEvent(
        "network_response",
        {
            path,
            method,
            status: res.status,
            durationMs: Date.now() - startedAt
        },
        res.status >= 500 ? "error" : res.status >= 400 ? "warn" : "info"
    );

    // On 401, attempt token refresh and retry once
    if (res.status === 401 && token) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            headers["Authorization"] = `Bearer ${newToken}`;
            let retryRes: Response;
            const retrySignal = buildSignal();
            try {
                retryRes = await fetch(`${getApiBase()}${path}`, {
                    ...options,
                    headers,
                    signal: retrySignal
                });
            } catch (e) {
                throw asApiErrorFromCatch(e, retrySignal);
            }
            if (!retryRes.ok) {
                const body = await retryRes.json().catch(() => ({}));
                const errEnvelope = body && typeof body.error === "object" ? body.error : undefined;
                notifyIfAccountInactive(retryRes.status, errEnvelope);
                throw new ApiError(
                    (typeof body.error === "string" ? body.error : body.error?.message) ||
                        `Request failed: ${retryRes.status}`,
                    "http",
                    retryRes.status,
                    errEnvelope
                );
            }
            return retryRes.json();
        }
        // Refresh returned null — a transient failure (network/timeout/5xx/429).
        // performRefresh now owns the logout-on-401 decision; we must NOT log
        // the nurse out here. Surface a retryable error instead of "Session
        // expired" so the UI doesn't push the user back to the login screen.
        throw new ApiError("Couldn't refresh your session. Please try again.", "network");
    }

    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const errEnvelope = body && typeof body.error === "object" ? body.error : undefined;
        notifyIfAccountInactive(res.status, errEnvelope);
        const apiErr = new ApiError(
            (typeof body.error === "string" ? body.error : body.error?.message) ||
                `Request failed: ${res.status}`,
            "http",
            res.status,
            errEnvelope
        );
        // Phase 6: auto-capture 5xx — 4xx is user/validation noise and the
        // component already gets a chance to handle it via the throw path.
        if (res.status >= 500) {
            captureSentryException(apiErr);
        }
        throw apiErr;
    }

    return res.json();
}

// --- Available Visits (Explore Tab) ---

export interface VisitRequestInfo {
    id: number;
    proposedDate: string;
    proposedTime: string;
}

/**
 * How THIS nurse stands with a visit on the Explore map. Server-derived (see
 * `getAvailableVisits`) so the web / iOS / Android pin renderers all colour from
 * one field instead of each re-deriving it and drifting apart.
 *
 * - `open`      — nothing between her and it; free to request
 * - `pending`   — she asked, awaiting the agency/patient
 * - `rejected`  — she asked and was turned down (visit may be re-broadcast)
 * - `cancelled` — her request was cancelled
 * - `assigned`  — hers: assigned and confirmed. Opens the visit screen, not the
 *                 available-visit detail (which 404s once a visit is assigned).
 */
export type VisitRelation = "open" | "pending" | "rejected" | "cancelled" | "assigned";

/**
 * How precise a visit's location is (security plan PR-18, revised 2026-10-06).
 * The street address ("exact") arrives only once the visit is ASSIGNED to her.
 * Before that she gets the patient's nearest cross street plus the area, pinned
 * at the intersection ("cross_street"), or, when no cross street is stored,
 * the neighbourhood/borough + ZIP and a pin rounded to 3 decimals, at most
 * about 70 m off ("approximate").
 */
export type LocationPrecision = "exact" | "cross_street" | "approximate";

/** When this nurse first and last opened the visit's detail screen. */
export interface VisitViewInfo {
    firstViewedAt: string;
    lastViewedAt: string;
    viewCount: number;
}

export interface AvailableVisitListItem {
    id: number;
    // Patient is masked to initials unless the visit is genuinely hers — she is
    // assigned to it, or her own request is still pending. A rejected or
    // cancelled request falls back to initials; `patientName` is null then.
    patientInitials: string;
    patientName: string | null;
    visitType: string;
    visitTypeDisplayName?: string | null;
    payment: number;
    /** The street address once the visit is assigned to her. Before that (a
     *  pending request included), and always for an applicant, the server sends
     *  "<cross street>, <area>" with `lat`/`lng` at the intersection, or just
     *  the area label ("Flushing, NY 11355") with `lat`/`lng` rounded to 3
     *  decimals: see `locationPrecision`. Render it through `visitLocationLine`
     *  (lib/visit-location.ts), not raw. */
    address: string;
    /** The coarse area label ("" when unknown). Absent from an API that
     *  predates it. */
    area?: string;
    /** "A & B" when `locationPrecision` is "cross_street", else null. */
    crossStreet?: string | null;
    /** "cross_street" = cross street + area, pin at the intersection;
     *  "approximate" = area + rounded pin only. Absent = an older API, which
     *  always sent the exact address. */
    locationPrecision?: LocationPrecision;
    /** Measured to the pin she is shown (the intersection or the rounded one
     *  when not exact). */
    distanceMiles: number;
    durationMinutes: number;
    scheduledStart: string | null;
    scheduledDue: string | null;
    /** Visit was created in the last 48h — a property of the visit, NOT of
     *  whether this nurse has seen it. For "new to her", use `viewed == null`.
     *  The Explore map/cards no longer render this (it competed with the
     *  "never opened" dot); Smart Picks still does. */
    isNew: boolean;
    lat: number;
    lng: number;
    relation: VisitRelation;
    /** Only ever her PENDING request; settled outcomes live in `relation`. */
    request: VisitRequestInfo | null;
    /** null = she has never opened this visit's detail screen. */
    viewed: VisitViewInfo | null;
}

/** One of the RN's own commitments on a given day — a confirmed visit already
 *  on her books or a still-pending request she has out. Surfaced so she can see
 *  her day while picking a time. */
export interface DayScheduleItem {
    kind: "confirmed" | "pending";
    /** 12-hour labels, e.g. "10:00 AM". */
    startLabel: string;
    endLabel: string;
    patientName: string;
    visitType: string | null;
}

export interface AvailableDateSlot {
    date: string;
    isoDate: string;
    dayName: string;
    window: string;
    timeSlots: string[];
    /** The RN's own visits/requests on this day (confirmed + pending), sorted by
     *  time. Empty when she has nothing else booked that day. */
    schedule: DayScheduleItem[];
    /** Subset of `timeSlots` that collide with `schedule` — either overlapping a
     *  commitment or falling within the 15-minute travel/rest buffer on either
     *  side. Rendered greyed-out and unpickable. */
    unavailableTimes: string[];
}

export interface AvailableVisitDetail extends AvailableVisitListItem {
    patientAge: number;
    patientGender: string;
    patientLanguage: string | null;
    availableDates: AvailableDateSlot[];
}

export function fetchAvailableVisits(): Promise<AvailableVisitListItem[]> {
    return request("/available-visits");
}

export function fetchAvailableVisit(id: number): Promise<AvailableVisitDetail> {
    return request(`/available-visits/${id}`);
}

/** Record that the nurse opened this visit's detail screen — clears the "new to
 *  you" dot on the Explore map and feeds the per-visit view counters. */
export function markAvailableVisitViewed(id: number): Promise<{ success: true }> {
    return request(`/available-visits/${id}/viewed`, { method: "POST" });
}

// ── Suggested visits ("Best for you") ──────────────────────────────────
// Server-ranked picks from the broadcasted pool. Every suggestion is already
// verified to fit the nurse's schedule (15-min buffer both sides) and carries
// the concrete bookable date + start time plus human-readable reasons.

/** One bookable placement of a visit — a date + start. */
export interface SuggestionOption {
    isoDate: string;
    dateLabel: string;
    dayName: string;
    time: string;
    timeLabel: string;
    endLabel: string;
    score: number;
    reasons: string[];
    anchorDistanceMiles: number | null;
}

export interface VisitSuggestion extends SuggestionOption {
    id: number;
    patientInitials: string;
    visitType: string;
    visitTypeDisplayName: string | null;
    payment: number;
    /** Never the street address for a suggestion (an open offer is never hers):
     *  "<cross street>, <area>" or the area label. */
    address: string;
    area?: string;
    crossStreet?: string | null;
    locationPrecision?: LocationPrecision;
    durationMinutes: number;
    isNew: boolean;
    homeDistanceMiles: number | null;
    /** The same visit placed on other days it also fits (best start per day). */
    otherOptions: SuggestionOption[];
    /** Alternate start labels on the primary day. */
    altTimes: string[];
    /** Day-plan mode only: part of the composed plan for the chosen day. */
    inPlan?: boolean;
}

/** Nurse preferences — all hard filters on the server. */
export interface SuggestionPrefs {
    date?: string;
    limit?: number;
    maxMiles?: number;
    minPay?: number;
    window?: "morning" | "afternoon" | "evening";
}

export interface SuggestedDay {
    isoDate: string;
    dayName: string;
    dateLabel: string;
    /** The nurse's own commitments that day, so the screen renders suggestions
     *  slotted into her real schedule rather than as an abstract list. */
    schedule: DayScheduleItem[];
    suggestions: VisitSuggestion[];
}

export interface VisitSuggestionsResponse {
    days: SuggestedDay[];
    totals: {
        suggestions: number;
        pay: number;
        openVisits: number;
        /** Day-plan mode only. */
        planCount?: number;
        planPay?: number;
    };
}

export function fetchVisitSuggestions(params?: SuggestionPrefs): Promise<VisitSuggestionsResponse> {
    const qs = new URLSearchParams();
    for (const key of ["date", "limit", "maxMiles", "minPay", "window"] as const) {
        const value = params?.[key];
        if (value != null) {
            qs.set(key, String(value));
        }
    }
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return request(`/available-visits/suggestions${suffix}`);
}

export interface AskSuggestionsResponse extends VisitSuggestionsResponse {
    /** One friendly sentence echoing what the system understood. */
    interpretation: string;
    /** False when the parser was unavailable and the default feed is shown. */
    understood: boolean;
}

/** Free-text ask: the nurse writes what she wants in her own words; the
 *  server parses it (LLM) and runs the same schedule-safe matching. */
export function askVisitSuggestions(query: string): Promise<AskSuggestionsResponse> {
    return request("/available-visits/suggestions/ask", {
        method: "POST",
        body: JSON.stringify({ query })
    });
}

// ── The nurse's own visit requests ─────────────────────────────────────
// A "request" is an offer the nurse made on a broadcast visit. `GET
// /visit-requests` returns her pending offers plus recently
// accepted/cancelled/rejected ones (last 30 days). `assignedToMe` says whether
// the underlying visit is still hers (so the UI knows whether tapping should
// open the visit).

export type VisitRequestStatus = "pending" | "accepted" | "cancelled" | "rejected";

export interface VisitRequestListItem {
    requestId: number;
    visitId: number;
    status: VisitRequestStatus;
    /** Plain-English summary of `status` (e.g. "awaiting agency review"). */
    outcome: string;
    patientName: string;
    /** Street address only once the visit is assigned to her; before that
     *  (pending included) the cross street + area, or the area
     *  (`locationPrecision`). */
    address: string | null;
    area?: string;
    crossStreet?: string | null;
    locationPrecision?: LocationPrecision;
    visitType: string | null;
    visitTypeDisplayName?: string | null;
    /** Nurse pay for the visit, in dollars. */
    payment: number;
    /** "YYYY-MM-DD" — the day the nurse proposed to do the visit. */
    proposedDate: string;
    /** "HH:MM" (24h) — the proposed start time. */
    proposedTime: string;
    createdAt: string | null;
    respondedAt: string | null;
    /** True when the underlying visit is currently assigned to this RN. */
    assignedToMe: boolean;
}

export function fetchVisitRequests(): Promise<VisitRequestListItem[]> {
    return request("/visit-requests");
}

export function requestVisit(
    id: number,
    proposedDate: string,
    proposedTime: string
): Promise<{ requestId: number }> {
    return request(`/available-visits/${id}/request`, {
        method: "POST",
        body: JSON.stringify({ proposedDate, proposedTime })
    });
}

// ── Compliance eligibility (visit-date gate) ───────────────────────────
// The /available-visits/:id/request endpoint can reject the call with a
// 409 `VISIT_COMPLIANCE_BLOCKED` when one or more compliance items would
// not be valid on the visit date. The mobile screen catches this via
// `extractVisitComplianceBlockers` and renders a popup listing the
// reasons instead of the generic "Failed to submit request" notification.

export type VisitComplianceBlockerReason =
    "missing" | "not_compliant" | "expires_before_visit" | "not_effective_yet" | "pending_review";

export interface VisitComplianceBlocker {
    itemId: number;
    itemName: string;
    reason: VisitComplianceBlockerReason;
    /** ISO date — only meaningful when `reason === "not_effective_yet"`. */
    effectiveDate: string | null;
    /** ISO date — only meaningful when `reason === "expires_before_visit"`. */
    expiryDate: string | null;
    /** Underlying compliance record status. */
    status: string;
    /** Non-null when the blocker is part of an OR-group; UI groups by this. */
    orGroupId: number | null;
    /**
     * Set when the blocker is cleared by finishing a training (the yearly
     * scope-of-practice refresher): the dialog offers "Start training".
     * Optional because older servers do not send it.
     */
    trainingBundleSlug?: string | null;
}

export interface VisitComplianceBlocked {
    /** The visit date the gate was computed against ("YYYY-MM-DD"). */
    asOfDate: string;
    blockers: VisitComplianceBlocker[];
}

/**
 * Returns the structured payload when the request failed because the
 * compliance gate blocked it. Returns `null` for any other error.
 */
export function extractVisitComplianceBlockers(e: unknown): VisitComplianceBlocked | null {
    if (!(e instanceof ApiError) || e.status !== 409 || !e.data) {
        return null;
    }
    if (e.data.code !== "VISIT_COMPLIANCE_BLOCKED") {
        return null;
    }
    const asOfDate = typeof e.data.asOfDate === "string" ? e.data.asOfDate : "";
    const blockers = Array.isArray(e.data.blockers)
        ? (e.data.blockers as VisitComplianceBlocker[])
        : [];
    if (!asOfDate || blockers.length === 0) {
        return null;
    }
    return { asOfDate, blockers };
}

/** A clock-in refused because the nurse is still clocked in to another visit. */
export interface OpenVisitConflict {
    message: string;
    openVisitId: number;
}

/**
 * Returns the open visit when a clock-in was refused because she is still
 * clocked in elsewhere (server code OPEN_VISIT_CLOCKED_IN). `null` for any other
 * error.
 */
export function extractOpenVisitConflict(e: unknown): OpenVisitConflict | null {
    if (!(e instanceof ApiError) || e.status !== 409 || !e.data) {
        return null;
    }
    if (e.data.code !== "OPEN_VISIT_CLOCKED_IN") {
        return null;
    }
    const openVisitId = Number(e.data.openVisitId);
    if (!Number.isInteger(openVisitId) || openVisitId <= 0) {
        return null;
    }
    const message =
        typeof e.data.message === "string" && e.data.message
            ? e.data.message
            : "You're still clocked in to another visit.";
    return { message, openVisitId };
}

// --- Visit endpoints ---

export interface VisitListItem {
    id: string;
    patientId: string;
    agencyId: string;
    rnId: string;
    visitTypeId: string;
    visitType: string | null;
    visitTypeDisplayName?: string | null;
    durationMinutes: number | null;
    formVersionId: string;
    status: string;
    clockInAt: string | null;
    clockOutAt: string | null;
    createdAt: string;
    startTime: string | null;
    endTime: string | null;
    patientFirstName: string;
    patientLastName: string;
    patientAddress: string | null;
    missedVisit: boolean;
    missedVisitReason: string | null;
    missedVisitFault: string | null;
    openForResubmission: boolean;
}

export function fetchVisits(): Promise<VisitListItem[]> {
    return request("/visits");
}

export interface FormDefinition {
    visit: {
        id: string;
        status: string;
        visitTypeId: string;
        formVersionId: string;
        serviceType: string | null;
        payerSource: string | null;
        frequency: string | null;
        clockInAt: string | null;
        clockOutAt: string | null;
        /** "Left before finishing" note; absent on older API builds. */
        clockOutNote?: string | null;
        /** When she actually tapped clock-out, if it was moved back to when she left (D27). */
        clockOutOriginalAt?: string | null;
        // Schedule & time
        scheduledStart: string | null;
        scheduledDue: string | null;
        startTime: string | null;
        endTime: string | null;
        timezone: string;
        // Payment
        payInCents: number;
        priceCents: number;
        // Caregiver — phones are exposed only as opaque contactRefs; the
        // device never holds the real number. Pass the contactRef back to
        // /visits/:id/contacts/:contactRef/call to bridge the call.
        caregiverName: string | null;
        caregiverContactRef: string | null;
        caregiverPhones: Array<{ contactRef: string; label: string | null; isPrimary: boolean }>;
        // The ONE exception to the masking above: on aide-evaluation visits
        // (Supervisory Visit / Performance Evaluation) the server ships the
        // aide's number in the clear so the nurse can confirm she is with the
        // right caregiver. null on every other visit. See lib/aide-contact.ts.
        caregiverPhoneNumber: string | null;
        // Clock location
        clockInLocation: { lat: number; lng: number } | null;
        clockOutLocation: { lat: number; lng: number } | null;
        // On-site issue (nurse arrived but blocked) + missed flags. While
        // onSiteIssue is set (and not missed), the screen shows the issue/chat
        // surface instead of the assessment.
        onSiteIssue: { reason: string; reportedAt: string; note?: string | null } | null;
        missedVisit: boolean;
        missedVisitReason: string | null;
        missedVisitFault: string | null;
        openForResubmission: boolean;
        resubmissionPurpose?: "assessment" | "rn_clarification" | null;
    };
    visitType: {
        id: string;
        name: string;
        displayLabel?: string | null;
    } | null;
    phases: Phase[];
    answers: Answer[];
    sectionStatuses: SectionStatusRecord[];
    agency: {
        /** Legal name (the Welcome Package prints it). */
        name: string;
        /** The name the patient knows the agency by (its DBA, else the legal name).
         *  Absent from servers older than this field. */
        publicName?: string;
        address: string | null;
        phone: string | null;
    } | null;
    patient: {
        firstName: string;
        lastName: string;
        gender: string | null;
        dob: string | null;
        // Demographics
        language: string | null;
        externalId: string | null;
        // Address
        address: string | null;
        addressLine2: string | null;
        addressInstructions: string | null;
        lat: number | null;
        lng: number | null;
        // Certification
        certificationPeriodStart: string | null;
        certificationPeriodEnd: string | null;
        // Phone numbers (from patient_phone_numbers table). Real numbers are
        // never sent to the device; use the contactRef with the bridge endpoint.
        phoneNumbers: Array<{
            contactRef: string;
            label: string;
            isPrimary: boolean;
            isSmsable: boolean | null;
        }>;
        // Profile data (from patient_profiles table)
        allergies: {
            medication: string[];
            food: string[];
            environmental: string[];
        } | null;
        emergencyContacts: Array<{
            contactRef: string;
            name: string | null;
            relationship: string | null;
            hasPhone: boolean;
        }>;
    } | null;
    // Special / Ad-Hoc visits only (kind=special). Standard visits omit both.
    // `formSource` discriminates the source; `orientation` is the read-only RN briefing
    // rendered at the top of the assessment.
    formSource?: "standard" | "special";
    orientation?: string | null;
}

export interface Phase {
    id: string;
    name: string;
    /** Server-resolved label: appends " – AM" / " – PM" only when the visit
     *  has two POC phases. Renderers should prefer this over `name`. */
    displayName?: string;
    /** 'AM' / 'PM' on split-shift POC phases (visit types 6, 7). NULL elsewhere. */
    shiftLabel?: string | null;
    orderIndex: number;
    sections: Section[];
}

export interface Section {
    id: string;
    name: string;
    /** Server-resolved label: appends " – AM" / " – PM" only when the visit
     *  actually has two POC sections. Renderers should prefer this over `name`. */
    displayName?: string;
    /** Mirrors the parent POC phase's shiftLabel; NULL on non-POC sections. */
    shiftLabel?: string | null;
    orderIndex: number;
    behaviorType: string;
    narrativeGuidanceText: string | null;
    visibleOnMobile: boolean;
    questions: Question[];
    aiTriggerConfigs: unknown[];
    staticContentBlocks: StaticContentBlock[];
}

export interface StaticContentBlock {
    id: string;
    sectionId: string;
    orderIndex: number;
    contentHtml: string;
    requiresAcknowledgment: boolean;
}

export interface AnswerOption {
    id: string;
    questionId: string;
    orderIndex: number;
    text: string;
    value: string;
}

export interface BranchingRule {
    id: string;
    sourceQuestionId: string;
    sourceAnswerValue: string;
    targetQuestionId: string;
    logicOperator:
        | "equals"
        | "includes"
        | "not_equals"
        | "greater_than"
        | "less_than"
        | "out_of_range"
        | "any_of";
}

export interface Question {
    id: string;
    text: string;
    type: string;
    orderIndex: number;
    mandatory: boolean;
    visibleOnMobile: boolean;
    /** Out of service. The API already omits these from the form payload; the
     *  field is here so the section screen can refuse to render one that slips
     *  through, and so a retired question is never counted as required-missing. */
    isRetired?: boolean;
    defaultValue: string | null;
    config: Record<string, unknown> | null;
    parentQuestionId: string | null;
    patientDataKey: string | null;
    hintText: string | null;
    answerOptions: AnswerOption[];
    branchingRules: BranchingRule[];
}

export interface Answer {
    id: string;
    visitId: string;
    questionId: string;
    value: string;
}

// Lifecycle-only — narrative comes from getSectionState (visit_section_narrative
// on the server), teaching from teaching_items, validation errors from
// visit_validation_errors. Older bundles destructuring narrativeText /
// teachingText / validationResult will see undefined; assessment + index
// screens read `status` only and aren't affected.
export interface SectionStatusRecord {
    id: string;
    visitId: string;
    sectionId: string;
    status: string;
    completedAt: string | null;
}

// --- AI result types ---

export interface ValidationError {
    id?: number;
    questionId: string | null;
    sectionId: string;
    ruleId?: string;
    message: string;
    severity: "suggested" | "hard";
}

export interface ICDSuggestion {
    code: string;
    description: string;
}

export interface SectionAIResult {
    sectionId: string;
    validationResult: {
        errors: ValidationError[];
        /**
         * Server-clock timestamp when this validation run was committed
         * (set by `saveValidationErrors`). Used by `useValidationFlow` to
         * keep its `lastValidationComputedAtRef` in sync after a successful
         * run, so the next run's Bug-10 recovery has a correct baseline
         * even if no focus refresh happens between runs. Not always present
         * (cache-hit path on `/sections/:id/complete` includes it; the
         * legacy mock path may not).
         */
        computedAt?: string;
    };
    icdSuggestions: ICDSuggestion[];
    teachingSuggestion: string | null;
}

export function fetchVisitForm(visitId: string): Promise<FormDefinition> {
    return request(`/visits/${visitId}/form`);
}

/** Where a chat banner's visit is (security plan PR-18 rule, decided by the API). */
export interface VisitPreviewLocation {
    /** The street address when `locationPrecision` is "exact", else the cross
     *  street and area, or the area. */
    address: string;
    area: string;
    crossStreet: string | null;
    locationPrecision: LocationPrecision;
}

export interface VisitPreview {
    id: number;
    /** The name as the API allows it (`patientNameForm`): the real first and
     *  last name when the visit is hers or her own request is pending; first
     *  name and last INITIAL ("I.") when she held it before; empty strings
     *  otherwise (show `patientInitials`). */
    patientFirstName: string;
    patientLastName: string;
    /** Absent from an older API. */
    patientNameForm?: "full" | "first_last_initial" | "initials";
    /** Masked identity (e.g. "IL"), always present. */
    patientInitials: string;
    scheduledStart: string | null;
    scheduledDue: string | null;
    startTime: string | null;
    endTime: string | null;
    timezone: string;
    status: string;
    isBroadcasted: boolean;
    /** True when the current RN is assigned, OR the visit is pending+broadcasted
     *  (so the RN may still claim it). The ticket chat banner gates navigation
     *  on this. */
    viewable: boolean;
    /** The visit is assigned to her now. Absent from an older API. */
    assignedToMe?: boolean;
    /** She held it before and it is not hers now. Absent from an older API. */
    wasAssignedToMe?: boolean;
    /** Null when she has no link to the visit; absent from an older API. */
    location?: VisitPreviewLocation | null;
}

export function fetchVisitPreview(visitId: number | string): Promise<VisitPreview> {
    return request(`/visits/${visitId}/preview`);
}

export interface InitiateVisitCallResult {
    callId: number;
    status: "dialing";
    contactName: string;
}

/**
 * Server-bridged click-to-call: the API tells Twilio to ring the RN's phone,
 * then bridge in the contact. The contact's real phone number is never sent
 * to the device — only the opaque contactRef returned in the form payload.
 */
export function initiateVisitCall(
    visitId: string,
    contactRef: string
): Promise<InitiateVisitCallResult> {
    return request(`/visits/${visitId}/contacts/${encodeURIComponent(contactRef)}/call`, {
        method: "POST"
    });
}

// lat/lng optional: a no-GPS clock-in/out carries only a `locationNote` reason.
// `quality` describes HOW the fix was obtained (how many readings we saw, how
// many we rejected as cached, whether the OS flagged it as mocked). Optional
// because builds before 2026-09 send none, and it is evidence about the point
// rather than part of it.
type ClockPayload = {
    lat?: number;
    lng?: number;
    accuracy?: number;
    quality?: CaptureQuality;
    locationNote?: string;
};

function clockBody(location?: ClockPayload): string {
    if (!location) {
        return JSON.stringify({});
    }
    return JSON.stringify({
        ...(location.lat != null && location.lng != null
            ? { lat: location.lat, lng: location.lng }
            : {}),
        ...(location.accuracy != null ? { accuracy: location.accuracy } : {}),
        ...(location.quality != null ? { quality: location.quality } : {}),
        ...(location.locationNote ? { locationNote: location.locationNote } : {})
    });
}

export function clockIn(
    visitId: string,
    location?: ClockPayload
): Promise<{ success: boolean; visit: any }> {
    return request(`/visits/${visitId}/clock-in`, {
        method: "POST",
        body: clockBody(location)
    });
}

export function clockOut(
    visitId: string,
    location?: ClockPayload
): Promise<{ success: boolean; visit: any }> {
    return request(`/visits/${visitId}/clock-out`, {
        method: "POST",
        body: clockBody(location)
    });
}

/** One entry in the visit location trail. See lib/visit-location-trail.ts. */
export interface VisitActivityEvent {
    kind: "gps_ping" | "app_foreground" | "app_background";
    occurredAt: string;
    lat?: number;
    lng?: number;
    accuracy?: number;
    trigger?: "interval" | "foreground" | "clock_in";
    blockReason?: "services_off" | "denied_permanent" | "denied_can_ask" | "no_fix";
    quality?: CaptureQuality;
}

/**
 * Flush a batch of trail events. The caller ignores the result: this is
 * evidence collection behind a nurse who is trying to do a visit, and it must
 * never be able to interrupt her.
 */
export function postVisitActivity(
    visitId: number,
    body: { appVersion?: string; events: VisitActivityEvent[] }
): Promise<{ accepted: number; skipped: number }> {
    return request(`/visits/${visitId}/activity`, {
        method: "POST",
        body: JSON.stringify(body)
    });
}

/**
 * Unified terminal action: clock out, and submit the assessment if every
 * section is complete. When sections are still incomplete the server REQUIRES
 * `note` (the reason she's leaving without finishing) and only clocks out —
 * `submitted` comes back false in that case.
 */
export function submitAndClockOut(
    visitId: string,
    params?: ClockPayload & { note?: string }
): Promise<{ success: boolean; visit: any; submitted: boolean }> {
    return request(`/visits/${visitId}/submit-and-clock-out`, {
        method: "POST",
        body: JSON.stringify({
            ...(params?.lat != null && params?.lng != null
                ? { lat: params.lat, lng: params.lng }
                : {}),
            ...(params?.accuracy != null ? { accuracy: params.accuracy } : {}),
            ...(params?.quality != null ? { quality: params.quality } : {}),
            ...(params?.locationNote ? { locationNote: params.locationNote } : {}),
            ...(params?.note ? { note: params.note } : {})
        })
    });
}

export type ReportIssueReason =
    | "patient_didnt_answer"
    | "patient_not_home"
    | "patient_refused"
    | "wrong_address"
    | "aide_not_here"
    | "other";

/**
 * Report an on-site issue (arrived but blocked). The server clocks the nurse in
 * (arrival proof), flags the visit, and opens a manager-bot chat — returns the
 * ticket id so the app can navigate straight into that conversation.
 */
export function reportVisitIssue(
    visitId: string,
    params: { reason: ReportIssueReason; note?: string; location?: { lat: number; lng: number } }
): Promise<{ ticketId: number; visit: any }> {
    return request(`/visits/${visitId}/report-issue`, {
        method: "POST",
        body: JSON.stringify({
            reason: params.reason,
            ...(params.note ? { note: params.note } : {}),
            ...(params.location ? { lat: params.location.lat, lng: params.location.lng } : {})
        })
    });
}

/** Clear an open on-site issue and resume the normal in-progress visit. */
export function resumeVisitIssue(visitId: string): Promise<{ visit: any }> {
    return request(`/visits/${visitId}/resume-issue`, {
        method: "POST",
        body: JSON.stringify({})
    });
}

export interface TravelTimeResult {
    mode: string;
    durationText: string;
    durationSeconds: number;
    distanceText: string;
    distanceMeters: number;
    polyline: string;
    rnLat: number;
    rnLng: number;
    patientLat: number;
    patientLng: number;
}

export function fetchTravelTime(
    visitId: string,
    mode: "driving" | "transit" | "walking"
): Promise<TravelTimeResult> {
    return request(`/visits/${visitId}/travel-time?mode=${mode}`);
}

export function saveAnswers(
    visitId: string,
    answers: { questionId: string; value: string }[]
): Promise<{
    success: boolean;
    savedAt: string;
    /**
     * The server deleted this visit's plan of care (and the supervisory hanging
     * off it) because the answer just saved feeds them. Optional — an older API
     * omits it, and `undefined` correctly means "nothing was invalidated".
     */
    derivedInvalidated?: boolean;
}> {
    return request(`/visits/${visitId}/answers`, {
        method: "POST",
        body: JSON.stringify({ answers }),
        // Well under the 120s default. An autosave is a small upsert, and the
        // section AI triggers and Complete now block on it — a stalled request
        // riding the default would freeze those buttons for two minutes. If it
        // aborts, nothing is lost: the answers stay dirty and the engine retries.
        timeoutMs: 15_000
    });
}

export function completeSection(
    visitId: string,
    sectionId: string
): Promise<{ success: boolean; validation_result: unknown }> {
    return request(`/visits/${visitId}/sections/${sectionId}/complete`, {
        method: "POST",
        body: "{}"
    });
}

export function completeSectionsBulk(
    visitId: string,
    sectionIds: string[]
): Promise<{ completed: number }> {
    return request(`/visits/${visitId}/sections/complete-bulk`, {
        method: "POST",
        body: JSON.stringify({ sectionIds })
    });
}

export function devAutoFill(
    visitId: string
): Promise<{ filled: number; sectionsCompleted: number }> {
    return request(`/visits/${visitId}/dev-auto-fill`, { method: "POST", body: "{}" });
}

export function submitForReview(visitId: string): Promise<{ success: boolean; visit: unknown }> {
    return request(`/visits/${visitId}/submit-for-review`, {
        method: "POST",
        body: "{}"
    });
}

// --- Patient diagnosis endpoints ---

export interface PatientDiagnosisItem {
    /**
     * `visit_diagnoses.id` for `source: "current_visit"` rows; `null` for
     * profile rows. Used by the mobile orchestrator to fire DELETE when an
     * RN un-selects a previously-attested code from Zone 2.
     */
    id: number | null;
    icdCode: string;
    description: string | null;
    source: "profile" | "current_visit";
    /**
     * For `source: "current_visit"` rows, the section the RN confirmed the
     * code under. `null` for `source: "profile"` (historical entries can
     * pre-date the section split or come from imports). The ICD step uses
     * this to only re-render THIS section's previously confirmed codes,
     * not codes confirmed under other sections of the same visit.
     */
    sectionId: number | null;
}

export function fetchPatientDiagnoses(visitId: string): Promise<PatientDiagnosisItem[]> {
    return request(`/visits/${visitId}/patient-diagnoses`);
}

/**
 * Static symptom→diagnosis matrix. Fetched once and cached by lib/diagnosis-map.
 * Drives the on-device "Suggested based on your answers" group (no LLM).
 */
export function fetchDiagnosisMap(): Promise<DiagnosisMapResponse> {
    return request(`/diagnosis-map`);
}

// --- Per-step AI endpoints ---

export function fetchIcdSuggestions(
    visitId: string,
    sectionId: string,
    opts?: { signal?: AbortSignal }
): Promise<SectionAIResult> {
    return request(`/visits/${visitId}/sections/${sectionId}/ai/icd`, {
        method: "POST",
        body: "{}",
        signal: opts?.signal
    });
}

export function fetchValidation(
    visitId: string,
    sectionId: string,
    opts?: { signal?: AbortSignal }
): Promise<SectionAIResult> {
    return request(`/visits/${visitId}/sections/${sectionId}/ai/validate`, {
        method: "POST",
        body: "{}",
        signal: opts?.signal
    });
}

export function fetchValidationErrors(
    visitId: string,
    sectionId: string
): Promise<ValidationError[]> {
    return request(`/visits/${visitId}/sections/${sectionId}/validation-errors`);
}

// Single source of truth for the section's authoritative state. Mobile calls
// this on every section focus to avoid stale local-cached narrative or
// missing validation errors after navigating away and back.
export interface SectionState {
    sectionId: number;
    status: "not_started" | "in_progress" | "completed";
    narrativeText: string;
    teachingText: string | null;
    completedAt: string | null;
    /**
     * Server-clock timestamp of the section's most recent validation run
     * (set by `saveValidationErrors`). NULL when the section has never been
     * validated. Used by `useValidationFlow`'s Bug-10 recovery to detect
     * whether the server actually committed during the current run when the
     * client never received the response.
     */
    validationComputedAt: string | null;
    validationErrors: ValidationError[];
}

export function fetchSectionState(visitId: string, sectionId: string): Promise<SectionState> {
    return request(`/visits/${visitId}/sections/${sectionId}/state`);
}

export function dismissValidationErrorApi(
    visitId: string,
    errorId: number
): Promise<{ dismissed: boolean }> {
    // Server returns `{ dismissed: false }` when the row is missing or
    // already dismissed (idempotent — Ship 3 / F1). Was `{ dismissed: true }`
    // pre-F1 with a 404 on the missing/duplicate path.
    return request(`/visits/${visitId}/validation-errors/${errorId}`, {
        method: "DELETE"
    });
}

// --- Per-step save endpoints ---

export function saveNarrative(
    visitId: string,
    sectionId: string,
    narrativeText: string
): Promise<{ saved: boolean }> {
    return request(`/visits/${visitId}/sections/${sectionId}/narrative`, {
        method: "PUT",
        body: JSON.stringify({ narrativeText })
    });
}

// --- Voice transcription endpoints ---

export function getAudioUploadUrl(
    visitId: string,
    sectionId: string,
    filename: string,
    contentType: string
): Promise<{ uploadUrl: string; s3Key: string }> {
    return request(`/visits/${visitId}/sections/${sectionId}/audio-upload-url`, {
        method: "POST",
        body: JSON.stringify({ filename, contentType })
    });
}

export function transcribeRecording(
    visitId: string,
    sectionId: string,
    s3Key: string
): Promise<{ transcript: string; confidence: number }> {
    return request(`/visits/${visitId}/sections/${sectionId}/transcribe`, {
        method: "POST",
        body: JSON.stringify({ s3Key })
    });
}

// --- Per-question voice recording (Option D in-card hero mic) ---
//
// Distinct from the section-level recorder above so the audit trails
// (question_voice_recordings vs ai_call_logs) stay independent.

export function getQuestionAudioUploadUrl(
    visitId: string,
    sectionId: string,
    questionId: string,
    filename: string,
    contentType: string
): Promise<{ uploadUrl: string; s3Key: string; recordingId: number }> {
    return request(
        `/visits/${visitId}/sections/${sectionId}/questions/${questionId}/audio-upload-url`,
        {
            method: "POST",
            body: JSON.stringify({ filename, contentType })
        }
    );
}

export function transcribeQuestionRecording(
    visitId: string,
    sectionId: string,
    questionId: string,
    recordingId: number,
    durationSeconds: number
): Promise<{ transcript: string; confidence: number }> {
    return request(`/visits/${visitId}/sections/${sectionId}/questions/${questionId}/transcribe`, {
        method: "POST",
        body: JSON.stringify({ recordingId, durationSeconds })
    });
}

export function saveDiagnoses(
    visitId: string,
    sectionId: string,
    codes: {
        code: string;
        description: string;
        suggestedByAi: boolean;
        source?: "ai" | "matrix" | "manual";
    }[]
): Promise<{
    saved: boolean;
    count: number;
    /**
     * The rows actually inserted this call (dedup-skipped codes are absent).
     * Mobile uses these to patch the local row with its real
     * `visit_diagnoses.id` so an immediate un-select can fire DELETE.
     */
    inserted: { id: number; icdCode: string }[];
}> {
    return request(`/visits/${visitId}/sections/${sectionId}/diagnoses`, {
        method: "POST",
        body: JSON.stringify({ codes })
    });
}

// --- Plan of Care endpoints ---

export interface PocDuty {
    id?: number;
    code: string | null;
    name: string;
    category: string;
    selected: boolean;
    frequency: string;
    agencyDefaultFrequency?: string | null;
    aiSuggested: boolean;
    rnModified?: boolean;
    rnChangeReasonCodes?: string[];
    rnChangeReasonNote?: string | null;
    uiLabel: string;
}

export type PocStatus = "pending" | "generated" | "failed" | "not_required" | "stale";

export interface PocUpdateAssessment {
    id: number;
    decision: "needed" | "not_needed";
    llmReason: string;
    reasonText: string;
    keyFindings: string[];
    llmCallId?: number | null;
    confirmedByRnAt?: string | null;
    rejectedByRnAt?: string | null;
}

/**
 * How the POC came into being on this visit, carried on every successful
 * GET /poc. "carry_forward" means a GENERAL special visit: nothing was
 * generated, the plan already on file was copied for the RN to amend — which is
 * what the duty list labels itself from.
 */
export interface PocMode {
    pocMode: "generate" | "carry_forward";
    rnMayDecline: boolean;
}

export interface PocData extends PocMode {
    pocId: string | null;
    status: PocStatus;
    duties: PocDuty[];
    specialInstructions?: string | null;
    dutyChangeSummary?: string | null;
    generatedAt?: string | null;
    confirmedByRnAt?: string | null;
    failureReason?: string | null;
    pocUpdateAssessment?: PocUpdateAssessment | null;
}

/**
 * Triggers async POC generation. Returns immediately with a pending job id.
 * The actual duties land via:
 *   1. WebSocket event "poc:generated" (real-time)
 *   2. fetchExistingPoc on focus (fallback)
 */
export interface GeneratePocResponse {
    jobId: number;
    status: "pending";
}

export function generatePocDuties(
    visitId: string,
    sectionId: string,
    opts?: { forceGenerate?: boolean }
): Promise<GeneratePocResponse> {
    return request(`/visits/${visitId}/sections/${sectionId}/ai/poc`, {
        method: "POST",
        body: JSON.stringify(opts ?? {})
    });
}

export function fetchExistingPoc(
    visitId: string,
    sectionId: string,
    opts?: { timeoutMs?: number }
): Promise<PocData> {
    return request(
        `/visits/${visitId}/sections/${sectionId}/poc`,
        opts?.timeoutMs ? { timeoutMs: opts.timeoutMs } : undefined
    );
}

export function savePocDuties(
    visitId: string,
    pocId: string,
    duties: {
        name: string;
        selected: boolean;
        frequency: string;
        rnChangeReasonCodes?: string[];
        rnChangeReasonNote?: string | null;
    }[],
    specialInstructions?: string
): Promise<{ saved: boolean }> {
    return request(`/visits/${visitId}/poc/${pocId}/duties`, {
        method: "PUT",
        body: JSON.stringify({ duties, specialInstructions })
    });
}

export function confirmPoc(
    visitId: string,
    pocId: string,
    signatureData?: string
): Promise<{ confirmed: boolean; confirmedByRnAt: string }> {
    return request(`/visits/${visitId}/poc/${pocId}/confirm`, {
        method: "POST",
        body: JSON.stringify(signatureData ? { signatureData } : {})
    });
}

export function confirmPocUpdateAssessment(
    visitId: string,
    sectionId: string,
    assessmentId: number,
    reasonText?: string
): Promise<{
    confirmed: boolean;
    assessmentId: number;
    confirmedByRnAt: string;
    reasonText: string;
}> {
    return request(
        `/visits/${visitId}/sections/${sectionId}/poc-update-assessment/${assessmentId}/confirm`,
        {
            method: "POST",
            body: JSON.stringify(reasonText !== undefined ? { reasonText } : {})
        }
    );
}

// --- Supervisory Evaluation endpoints ---

export interface SupervisoryDutyItem {
    dutyName: string;
    supervisoryLabel: string;
    category: string;
}

export interface SupervisoryEvaluationData {
    evaluationId: string;
    isAidePresent: boolean | null;
    actualAideName: string | null;
    absenceReasonTags: string[];
    absenceReasonNote: string | null;
    directObservationDuties: string[];
    directObservationCompetencies: string[];
    taskRatings: {
        dutyName: string;
        supervisoryLabel: string;
        rating: string;
        isDirectObservation: boolean;
    }[];
    competencyRatings: {
        competencyName: string;
        rating: string;
        isDirectObservation: boolean;
    }[];
    notes: { observationNotes: string | null; supervisorComments: string | null } | null;
    rnSignatureData: string | null;
    caregiverSignatureData: string | null;
}

export function fetchSupervisoryDuties(
    visitId: string
): Promise<{ duties: SupervisoryDutyItem[]; source: string }> {
    return request(`/visits/${visitId}/supervisory-evaluation/duties`);
}

export interface SupervisoryCompetencyItem {
    name: string;
    mandatory: boolean;
}

export function fetchSupervisoryCompetencies(
    visitId: string
): Promise<{ competencies: SupervisoryCompetencyItem[]; source: string }> {
    return request(`/visits/${visitId}/supervisory-evaluation/competencies`);
}

export function fetchSupervisoryEvaluation(visitId: string): Promise<SupervisoryEvaluationData> {
    return request(`/visits/${visitId}/supervisory-evaluation`);
}

export function saveSupervisoryEvaluation(
    visitId: string,
    data: {
        isAidePresent: boolean;
        actualAideName?: string;
        absenceReasonTags?: string[];
        absenceReasonNote?: string;
        directObservationDuties: string[];
        directObservationCompetencies: string[];
        taskRatings: { dutyName: string; rating: string }[];
        competencyRatings: { competencyName: string; rating: string }[];
        observationNotes?: string;
        supervisorComments?: string;
        rnSignatureData?: string;
        caregiverSignatureData?: string;
        isComplete?: boolean;
    }
): Promise<{ saved: boolean; evaluationId: string }> {
    return request(`/visits/${visitId}/supervisory-evaluation`, {
        method: "PUT",
        body: JSON.stringify(data)
    });
}

// --- Annual Field and Administrative Performance Evaluation ---

export interface PerformanceEvaluationItem {
    id: string;
    sourceType: "master" | "custom";
    sourceId: number;
    itemKey: string | null;
    category: "general" | "administrative";
    label: string;
    serviceTypes: string[];
    enabled: boolean;
    sortOrder: number;
    isCustomized: boolean;
}

export interface PerformanceEvaluationRatingInput {
    sourceType: "master" | "custom";
    sourceId: number;
    itemKey?: string | null;
    category: "general" | "administrative";
    label: string;
    order: number;
    rating: "competent" | "needs_improvement";
    methods: ("verbal" | "observation")[];
    note?: string;
}

export interface PerformanceEvaluationData {
    evaluation: {
        id: number;
        isAidePresent: boolean | null;
        actualAideName: string | null;
        absenceReasonTags: string[];
        absenceReasonNote: string | null;
        generalComments: string | null;
        administrativeComments: string | null;
        supervisorComments: string | null;
    } | null;
    ratings: PerformanceEvaluationRatingInput[];
    rnSignatureData: string | null;
    caregiverSignatureData: string | null;
    caregiverSignerName: string | null;
}

export function fetchPerformanceEvaluationItems(
    visitId: string
): Promise<{ items: PerformanceEvaluationItem[]; source: string }> {
    return request(`/visits/${visitId}/performance-evaluation/items`);
}

export function fetchPerformanceEvaluation(visitId: string): Promise<PerformanceEvaluationData> {
    return request(`/visits/${visitId}/performance-evaluation`);
}

export function savePerformanceEvaluation(
    visitId: string,
    data: {
        isAidePresent: boolean;
        actualAideName?: string;
        absenceReasonTags?: string[];
        absenceReasonNote?: string;
        generalComments?: string;
        administrativeComments?: string;
        supervisorComments?: string;
        ratings: PerformanceEvaluationRatingInput[];
        rnSignatureData?: string;
        caregiverSignatureData?: string;
        caregiverSignerName?: string;
        isComplete?: boolean;
    }
): Promise<{ saved: boolean; evaluationId: number }> {
    return request(`/visits/${visitId}/performance-evaluation`, {
        method: "PUT",
        body: JSON.stringify(data)
    });
}

// --- Medication endpoints ---

export interface Medication {
    id: string;
    visitId: string;
    medicationName: string;
    dosage: string | null;
    strength: string | null;
    strengthNotApplicable?: boolean;
    orderedDose: string | null;
    frequency: string | null;
    administrationNotes: string | null;
    maxDailyDose?: string | null;
    sourceImageUrl: string | null;
    isRemoved: boolean;
    isSupplement: boolean;
    route: string | null;
    diagnosisCodes: MedicationDiagnosisCode[];
    /**
     * This medication came from a photo whose attribution survived the save, so
     * the card can offer the viewer. False for anything typed by hand, dictated,
     * or carried forward from a previous visit.
     */
    hasSourceImages?: boolean;
}

export interface MedicationDiagnosisCode {
    id: string;
    visitMedicationId: string;
    icdCode: string;
    description: string | null;
    type: string;
}

export interface MedicationDraftProductSuggestion {
    name: string;
    label: string;
    route: string | null;
    strength: string | null;
    form: string | null;
    strengthForm: string | null;
    isSupplement?: boolean;
    rows: number;
    source: string;
    doses: string[];
    frequencies: string[];
    administrationNotes: string[];
}

export type MedicationPickerOption = MedicationDraftProductSuggestion;

export interface MedicationDraftNormalizationSuggestions {
    medicationOptions: MedicationDraftProductSuggestion[];
    doses: string[];
    frequencies: string[];
    administrationNotes: string[];
}

export interface MedicationDraft {
    id: string;
    visitId: string;
    patientId: string;
    name: string;
    medicationName: string;
    dosage: string | null;
    strength: string | null;
    orderedDose: string | null;
    frequency: string | null;
    administrationNotes: string | null;
    maxDailyDose?: string | null;
    jobId: string | null;
    promotedToMedicationId: string | null;
    isSupplement?: boolean;
    route?: string | null;
    sourceImageUrl: string | null;
    /**
     * Who took the photo this card was read from: `rn` (the nurse, default) or
     * `patient_photo` (sent by the patient through the pre-visit questionnaire
     * and imported from the Medications section). OPTIONAL: older API builds
     * omit it; absent reads as `rn`.
     */
    origin?: "rn" | "patient_photo";
    // Consensus fields
    consensusConfidence?: "high" | "medium";
    consensusScore?: number;
    appearedInRuns?: number[];
    /**
     * 1-based positions of the uploaded photos this medication was read from.
     * Several when a wrapped label was shot in halves. Absent on voice drafts and
     * on anything extracted before the model was asked for it.
     */
    sourceImageIndexes?: number[] | null;
    mergeNotes?: string;
    /**
     * Highest `likelihood` first (sorted server-side). `likelihood` is the
     * model's 0-100 estimate of how often the code is the real indication for
     * the drug; absent on drafts written before the field existed.
     */
    suggestedIcdCodes?: { code: string; description: string; likelihood?: number }[];
    normalizationSuggestions?: MedicationDraftNormalizationSuggestions;
}

export interface AISuggestions {
    strengths?: string[];
    frequencies: string[];
    administrationNotes: string[];
    /** Highest `likelihood` first — see MedicationDraft.suggestedIcdCodes. */
    icdCodes: { code: string; description: string; likelihood?: number }[];
    doses?: string[];
}

export function fetchMedications(visitId: string): Promise<Medication[]> {
    return request(`/visits/${visitId}/medications`);
}

/**
 * Signed URLs for the photos one draft was read from. Fetched on demand when the
 * nurse taps the image button, not with the draft list — the URLs are short-lived
 * and most cards are never opened.
 */
/** One source photo behind a draft, as returned by the draft-images endpoint. */
export interface DraftSourceImage {
    /** 1-based position in the batch the nurse uploaded. */
    index: number;
    url: string;
    /**
     * Where this medication sits on this photo, as fractions of the image with
     * the origin top-left. Null when the model could not place it, on drafts
     * predating boxes, and on anything the API is too old to send.
     */
    box?: { x: number; y: number; width: number; height: number } | null;
    /** How many of the extraction's medications were read from this same photo. */
    medicationCount?: number;
}

export function fetchDraftImages(
    visitId: string,
    draftId: string
): Promise<{ images: DraftSourceImage[] }> {
    return request(`/visits/${visitId}/medications/drafts/${draftId}/images`);
}

/**
 * The same photos, for a medication that has already been saved. Promoting a
 * draft keeps the row that carries the image attribution, so the label stays
 * reachable after the save — see `getMedicationImages` on the server.
 */
export function fetchMedicationImages(
    visitId: string,
    medicationId: string
): Promise<{ images: DraftSourceImage[] }> {
    return request(`/visits/${visitId}/medications/${medicationId}/images`);
}

export function searchMedicationLookup(
    visitId: string,
    query: string,
    limit = 12
): Promise<{ options: MedicationPickerOption[] }> {
    return request(
        `/visits/${visitId}/medications/lookup?query=${encodeURIComponent(query)}&limit=${limit}`
    );
}

export function createMedication(
    visitId: string,
    data: {
        medicationName: string;
        dosage?: string;
        strength?: string;
        strengthNotApplicable?: boolean;
        orderedDose?: string;
        frequency?: string;
        /** True when she typed her own wording rather than picking a chip. */
        frequencyIsCustom?: boolean;
        administrationNotes?: string;
        maxDailyDose?: string;
        sourceImageUrl?: string;
        isSupplement?: boolean;
        route?: string;
    }
): Promise<Medication> {
    return request(`/visits/${visitId}/medications`, {
        method: "POST",
        body: JSON.stringify(data)
    });
}

export function updateMedication(
    visitId: string,
    medicationId: string,
    data: {
        dosage?: string;
        strength?: string;
        strengthNotApplicable?: boolean;
        orderedDose?: string;
        frequency?: string;
        /** True when she typed her own wording rather than picking a chip. */
        frequencyIsCustom?: boolean;
        administrationNotes?: string;
        maxDailyDose?: string;
        route?: string;
        diagnosisCodes?: { icdCode: string; description?: string; type?: string }[];
    }
): Promise<Medication> {
    return request(`/visits/${visitId}/medications/${medicationId}`, {
        method: "PUT",
        body: JSON.stringify(data)
    });
}

export function deleteMedication(
    visitId: string,
    medicationId: string,
    deleteCodes = false
): Promise<{ deleted: boolean }> {
    const qs = deleteCodes ? "?deleteCodes=true" : "";
    return request(`/visits/${visitId}/medications/${medicationId}${qs}`, {
        method: "DELETE"
    });
}

export function saveMedicationDiagnosisCodes(
    visitId: string,
    medicationId: string,
    codes: { icdCode: string; description?: string }[]
): Promise<{ saved: boolean; count: number }> {
    return request(`/visits/${visitId}/medications/${medicationId}/diagnosis-codes`, {
        method: "POST",
        body: JSON.stringify({ codes })
    });
}

export function getUploadUrls(
    visitId: string,
    filenames: string[]
): Promise<{ urls: { uploadUrl: string; s3Key: string }[] }> {
    return request(`/visits/${visitId}/medications/upload-urls`, {
        method: "POST",
        body: JSON.stringify({ filenames })
    });
}

export function extractFromImages(
    visitId: string,
    s3Keys: string[]
): Promise<{ drafts: MedicationDraft[]; warnings: string[]; attemptId: string }> {
    return request(`/visits/${visitId}/medications/extract-from-images`, {
        method: "POST",
        body: JSON.stringify({ s3Keys })
    });
}

// ─── Medications by voice ───────────────────────────────────────────────
//
// Record, upload, one model call: drafts, exactly like the photo flow produces,
// so the review screen the nurse already knows is unchanged. `device_stt` is
// implemented but nothing in the UI selects it.

export type VoiceEngine = "device_stt" | "audio_native";

/** Presigned PUT for the recording. */
export function getVoiceUploadUrl(
    visitId: string,
    extension: "m4a" | "wav" | "webm" | "mp4"
): Promise<{ uploadUrl: string; s3Key: string; contentType: string }> {
    return request(`/visits/${visitId}/medications/voice/upload-url`, {
        method: "POST",
        body: JSON.stringify({ extension })
    });
}

export function extractFromVoice(
    visitId: string,
    body: { engine: "audio_native"; s3Key: string } | { engine: "device_stt"; transcript: string }
): Promise<{ status: string; attemptId: number }> {
    return request(`/visits/${visitId}/medications/extract-from-voice`, {
        method: "POST",
        body: JSON.stringify(body)
    });
}

export function fetchDrafts(visitId: string): Promise<MedicationDraft[]> {
    return request(`/visits/${visitId}/medications/drafts`);
}

// ─── Medications from the patient's own photos ──────────
//
// Photos the patient sent through the pre-visit questionnaire that were
// triaged as a label or a pill organiser have already been through the
// extraction pipeline (origin = 'patient_photo'). Their drafts stay out of
// `fetchDrafts` until the nurse imports them from the Medications hub.

export interface PatientPhotoMedications {
    /** Photos triaged as medication_label / pill_organiser. */
    photoCount: number;
    /** Uploaded but not yet triaged. */
    pendingPhotoCount: number;
    jobs: Array<{
        id: number;
        status: "pending" | "processing" | "completed" | "failed";
        draftCount: number;
        imported: boolean;
        photoId: number | null;
    }>;
    /** Finished, not yet imported, with at least one draft. */
    importableDraftCount: number;
    importedDraftCount: number;
    /** Extraction jobs still running. */
    readingCount: number;
}

export function fetchPatientPhotoMedications(visitId: string): Promise<PatientPhotoMedications> {
    return request(`/visits/${visitId}/patient-photos/medications`);
}

export function importPatientPhotoMedications(
    visitId: string
): Promise<{ importedJobIds: number[]; draftCount: number; stillReading: number }> {
    return request(`/visits/${visitId}/patient-photos/medications/import`, { method: "POST" });
}

// Latest extraction-job status for the visit. The medications hub reads this on
// focus to recover in-progress / failed state after navigating away — the WS
// event (medication-extraction:completed/failed) is best-effort and lost if the
// screen unmounted while the job ran. Mirrors the POC pattern.
export interface MedicationExtractionStatus {
    status: "none" | "pending" | "processing" | "completed" | "failed";
    failureReason: string | null;
    // WHICH job this status describes, so the hub can dismiss exactly the
    // failure it is showing. OPTIONAL: an app build newer than the API must
    // keep working, and dismissal falls back to "the visit's latest job".
    jobId?: number | null;
    // Why it failed, in the only two flavours worth telling the nurse apart:
    // "no_medications" (the photos were read and held nothing we could
    // identify — retake them) vs "error" (the pipeline broke — retry the same
    // photos). OPTIONAL for the same reason; absent reads as "error".
    failureKind?: MedicationExtractionFailureKind | null;
    // True once she has dismissed this job's failure. The hub re-derives its
    // failure state from the server on every focus, so this is the only thing
    // that stops a dead job greeting her for the rest of the visit.
    dismissed?: boolean;
    // ISO timestamp of the latest job; null when status === "none". The client
    // applies its own staleness cutoff against this for orphaned `processing`
    // rows the server-side reaper never reached.
    createdAt: string | null;
    // Photos attached to the latest job, so the hub can tell the RN how many are
    // being read. OPTIONAL on purpose: an app build newer than the API (or the
    // reverse) must keep working, so every read guards on it being present.
    photoCount?: number | null;
}

export function fetchExtractionStatus(visitId: string): Promise<MedicationExtractionStatus> {
    return request(`/visits/${visitId}/medications/extraction-status`);
}

/**
 * Stop the hub surfacing one extraction failure, for good.
 *
 * Clearing it client-side only lasts until the screen unmounts — the hub
 * rebuilds its failure state from the job row on the next focus. The dismissal
 * has to live on the row, so it is stored server-side. A later extraction gets
 * a new row, so dismissing this failure never hides the next one.
 */
export function dismissExtractionJob(
    visitId: string,
    jobId?: string | number | null
): Promise<{ dismissed: boolean; jobId: number | null }> {
    return request(`/visits/${visitId}/medications/extraction-status/dismiss`, {
        method: "POST",
        body: JSON.stringify(jobId != null ? { jobId: Number(jobId) } : {})
    });
}

export function promoteDraft(
    visitId: string,
    draftId: string,
    data: {
        medicationName?: string;
        route?: string;
        dosage?: string;
        strength?: string;
        strengthNotApplicable?: boolean;
        orderedDose?: string;
        frequency?: string;
        /** True when she typed her own wording rather than picking a chip. */
        frequencyIsCustom?: boolean;
        administrationNotes?: string;
        maxDailyDose?: string;
        isSupplement?: boolean;
        diagnosisCodes?: { icdCode: string; description?: string }[];
    } = {}
): Promise<{ medication: Medication; promoted: boolean }> {
    return request(`/visits/${visitId}/medications/drafts/${draftId}/promote`, {
        method: "POST",
        body: JSON.stringify(data)
    });
}

export function deleteDraft(visitId: string, draftId: string): Promise<{ deleted: boolean }> {
    return request(`/visits/${visitId}/medications/drafts/${draftId}`, {
        method: "DELETE"
    });
}

export function fetchAISuggestions(
    visitId: string,
    medicationName: string,
    dosage?: string,
    missingFields?: ("frequency" | "administrationNotes" | "icdCodes" | "doses" | "strengths")[],
    useNormalizedSuggestionChain = false,
    strength?: string,
    orderedDose?: string
): Promise<AISuggestions> {
    return request(`/visits/${visitId}/medications/ai-suggestions`, {
        method: "POST",
        body: JSON.stringify({
            medicationName,
            dosage,
            strength,
            orderedDose,
            missingFields,
            useNormalizedSuggestionChain
        })
    });
}

export function fetchICDSuggestionsForMed(
    visitId: string,
    medicationName: string,
    dosage?: string,
    strength?: string,
    orderedDose?: string
): Promise<{ icdCodes: { code: string; description: string }[] }> {
    return request(`/visits/${visitId}/medications/icd-suggestions`, {
        method: "POST",
        body: JSON.stringify({ medicationName, dosage, strength, orderedDose })
    });
}

// ─── Diagnosis Hub ─────────────────────────────────────

export interface DiagnosisHubSource {
    type: "section" | "med" | "agency";
    label: string;
    sourceId: number;
}

export interface DiagnosisHubItem {
    icdCode: string;
    description: string;
    classification: "principal" | "surgical" | "other";
    sources: DiagnosisHubSource[];
}

export interface DiagnosisHubResponse {
    patientId: number;
    diagnoses: DiagnosisHubItem[];
}

export function fetchDiagnosisHub(visitId: string): Promise<DiagnosisHubResponse> {
    return request(`/visits/${visitId}/diagnosis-hub`);
}

export function deleteVisitDiagnosisById(
    visitId: string,
    diagnosisId: number
): Promise<{ deleted: boolean }> {
    return request(`/visits/${visitId}/visit-diagnoses/${diagnosisId}`, {
        method: "DELETE"
    });
}

export function updateDiagnosisClassification(
    visitId: string,
    sourceType: "section" | "med" | "agency",
    sourceId: number,
    classification: "principal" | "surgical" | "other"
): Promise<{ updated: boolean }> {
    return request(`/visits/${visitId}/diagnosis-hub/classification`, {
        method: "PUT",
        body: JSON.stringify({ sourceType, sourceId, classification })
    });
}

// ─── Push Token ──────────────────────────────────────────

export function registerPushToken(
    rnId: number,
    token: string,
    platform: "ios" | "android"
): Promise<{ ok: boolean }> {
    return request(`/rn/${rnId}/push-token`, {
        method: "POST",
        body: JSON.stringify({ token, platform })
    });
}

export function deactivatePushTokens(rnId: number): Promise<{ ok: boolean }> {
    return request(`/rn/${rnId}/push-token`, {
        method: "DELETE"
    });
}

// ─── Payments (InstaPay) ────────────────────────────────

export interface RnPaymentsSummary {
    thisWeekCents: number;
    pendingCents: number;
    allTimePaidCents: number;
    /** Withheld pending a location check. Kept out of `pendingCents`. */
    onHoldCents: number;
}

export interface RnPaymentHistoryItem {
    visitId: number;
    visitDate: string | null;
    clockInAt: string | null;
    patientName: string;
    agencyName: string;
    amountCents: number;
    missedVisitPay: boolean;
    /**
     * A top-up for a visit already paid earlier, because its pay went up after
     * it was paid. The visit has its original row too; this row is the
     * difference only. Absent from servers older than this field.
     */
    payDifference?: boolean;
    paymentStatus: "pending" | "in_batch" | "paid" | "on_hold";
    batchId: number | null;
    /** on_hold only: the date it releases itself even if nobody reviews it. */
    holdAutoReleaseAt: string | null;
    /** on_hold only: what she has already told us, if anything. */
    holdExplanation: string | null;
}

/**
 * Server-owned notice for the payments screen (a banking-holiday delay, or a
 * manual override). The client renders whatever it is handed and computes
 * nothing: no dates, no copy, no decision about whether to show it. That is
 * what lets the whole surface be changed or switched off from the
 * `payroll_notifications` settings row in seconds, with no app release.
 *
 * Fields are semantic on purpose — `severity`, not colors. The moment the
 * server sends styling, restyling needs a build again.
 */
export interface RnPaymentNotice {
    id: string;
    severity: "info" | "warning";
    title: string;
    body: string;
    /** ISO instant. Past this the client hides the notice on its own. */
    expiresAt: string;
}

/** A joining bonus she has earned ($25 onboarding, $150 at 10 visits). */
export interface RnBonusEarned {
    id: number;
    bonusType: "onboarding" | "ten_visits";
    title: string;
    amountCents: number;
    /** YYYY-MM-DD, New York. */
    earnedOn: string;
    status: "paid" | "on_the_way" | "needs_bank";
    /** YYYY-MM-DD the deposit went out; paid only. */
    paidOn: string | null;
}

/** A joining bonus she can still win before its deadline. */
export interface RnBonusOffer {
    bonusType: "onboarding" | "ten_visits";
    title: string;
    amountCents: number;
    /** YYYY-MM-DD, New York; the whole day counts. */
    deadline: string;
    daysLeft: number;
    /** 10-visit bonus only. */
    progress: { done: number; required: number } | null;
}

export interface RnBonuses {
    earned: RnBonusEarned[];
    offers: RnBonusOffer[];
}

export interface RnPaymentsResponse {
    summary: RnPaymentsSummary;
    history: RnPaymentHistoryItem[];
    /** Null whenever there is nothing true to say. Optional: older API, no field. */
    notice?: RnPaymentNotice | null;
    /**
     * Her joining bonuses, in their own field (never rows in `history`). Null for
     * a nurse the rules never pay; absent from an older API. Either way: no card.
     */
    bonuses?: RnBonuses | null;
}

export function fetchRnPayments(limit = 50): Promise<RnPaymentsResponse> {
    return request(`/rn/payments?limit=${limit}`);
}

/**
 * Her own account of a held visit.
 *
 * Saving does not release the hold, and the screen says so. Implying otherwise
 * would be worse than saying nothing: a nurse who believes she has just fixed
 * it and then waits four more days has been misled by us, not by the delay.
 */
export function submitHoldExplanation(
    visitId: number,
    explanation: string
): Promise<{ saved: boolean }> {
    return request(`/rn/payments/holds/${visitId}/explanation`, {
        method: "POST",
        body: JSON.stringify({ explanation })
    });
}

// ─── Payment Statements ────────────────────────────────

export interface PaymentStatementResult {
    /** Inbox the statement was sent to — echoed so she knows where to look. */
    email: string;
    /** e.g. "July 2026". */
    periodLabel: string;
    visitCount: number;
    totalCents: number;
}

/**
 * Generates the nurse's payment statement for a month and emails it to her as a
 * PDF attachment. Slow by design (PDF render + send), so it gets a longer
 * timeout than the default.
 */
export function requestPaymentStatement(
    year: number,
    month: number
): Promise<PaymentStatementResult> {
    return request("/rn/payments/statement", {
        method: "POST",
        body: JSON.stringify({ year, month }),
        timeoutMs: 120_000
    });
}

// ─── Virtual ID Card ───────────────────────────────────

export interface IdCardAgency {
    id: number;
    name: string;
    logoUrl: string | null;
}

export interface IdCardResponse {
    rnId: number;
    fullName: string;
    role: "Registered Nurse";
    gender: string | null;
    licenseNumber: string | null;
    dob: string | null;
    hireDate: string | null;
    photoUrl: string | null;
    agencies: IdCardAgency[];
}

export function fetchRnIdCard(): Promise<IdCardResponse> {
    return request("/rn/me/id-card");
}

// ─── Profile photo (editable avatar) ───────────────────

export interface ProfilePhotoUploadTarget {
    uploadUrl: string;
    s3Url: string;
}

/** Step 1 — ask the API for a presigned PUT URL for the new profile photo. */
export function requestProfilePhotoUploadUrl(
    filename: string,
    contentType: string
): Promise<ProfilePhotoUploadTarget> {
    return request("/rn/me/profile/photo/upload-url", {
        method: "POST",
        body: JSON.stringify({ filename, contentType })
    });
}

/** Step 2 — confirm the upload; returns a presigned URL for immediate display. */
export function setProfilePhoto(s3Url: string): Promise<{ photoUrl: string }> {
    return request("/rn/me/profile/photo", {
        method: "PUT",
        body: JSON.stringify({ s3Url })
    });
}

/** Remove the current profile photo (reverts to selfie/initials fallback). */
export function removeProfilePhoto(): Promise<{ success: true }> {
    return request("/rn/me/profile/photo", { method: "DELETE" });
}

// ─── Teaching ──────────────────────────────────────────

export interface TeachingItem {
    id: number;
    visitId: number;
    sectionId: number;
    content: string;
    source: "ai" | "manual";
    status: "pending" | "generated" | "failed";
    failureReason: string | null;
    bullmqJobId: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface TeachingScreenResponse {
    items: TeachingItem[];
    pendingSections: { sectionId: number; sectionName: string }[];
    /**
     * Whether generation has ever finished for this visit. Distinguishes
     * "nobody has generated yet" from "generation ran and there is nothing to
     * teach" -- identical from `items` alone, and the screen needs the
     * difference to know whether the section can be completed.
     */
    generationAttempted: boolean;
}

export function fetchTeachingItems(visitId: string): Promise<TeachingScreenResponse> {
    return request(`/visits/${visitId}/teaching-items`);
}

export function updateTeachingItem(
    visitId: string,
    itemId: number,
    content: string
): Promise<TeachingItem> {
    return request(`/visits/${visitId}/teaching-items/${itemId}`, {
        method: "PATCH",
        body: JSON.stringify({ content })
    });
}

export function deleteTeachingItem(visitId: string, itemId: number): Promise<{ deleted: true }> {
    return request(`/visits/${visitId}/teaching-items/${itemId}`, {
        method: "DELETE"
    });
}

/**
 * Kick off the whole-visit teaching generation (one LLM call over the full
 * assessment). Async, mirroring Plan of Care: returns 202 with the job id; the
 * screen then waits on the `teaching:generated` / `teaching:failed` WS events (or
 * a focus-refresh) and reloads via fetchTeachingItems.
 */
export function generateTeaching(visitId: string): Promise<{ jobId: number; status: "pending" }> {
    return request(`/visits/${visitId}/teaching/generate`, {
        method: "POST"
    });
}

// --- RN phone numbers (multi-phone) ---

export interface RnPhoneEntry {
    id: number;
    number: string;
    label: string | null;
    isPrimary: boolean;
    verifiedAt: string | null;
    source: "admin" | "rn_self" | "legacy";
    createdAt: string;
    lastUsedAt: string | null;
}

export function fetchMyPhones(): Promise<{ phones: RnPhoneEntry[] }> {
    return request("/rn/me/phones");
}

export function startMyPhoneVerify(number: string): Promise<{ message: string }> {
    return request("/rn/me/phones/start-verify", {
        method: "POST",
        body: JSON.stringify({ number })
    });
}

export function confirmMyPhoneVerify(number: string, code: string): Promise<RnPhoneEntry> {
    return request("/rn/me/phones/confirm-verify", {
        method: "POST",
        body: JSON.stringify({ number, code })
    });
}

export function setMyPhonePrimary(phoneId: number): Promise<RnPhoneEntry> {
    return request(`/rn/me/phones/${phoneId}/primary`, { method: "POST" });
}

export function removeMyPhone(
    phoneId: number
): Promise<{ removedId: number; newPrimaryId: number | null }> {
    return request(`/rn/me/phones/${phoneId}`, { method: "DELETE" });
}

// --- Live Translation ---
//
// The mobile holds the only realtime path to OpenAI (WebRTC direct to
// /v1/realtime/translations/calls); these three endpoints just bracket the
// session so the server can persist metadata + a transcript and enforce
// per-RN minute budgets.

// Discriminated on `provider` — mirrors the backend CreateSessionOutput union.
// The OpenAI variant feeds the WebRTC hook; the Gemini variant feeds the
// dual-WebSocket hook. The transport hook is chosen at build time from
// EXPO_PUBLIC_LIVE_TRANSLATION_PROVIDER (see useActiveLiveTranslationSession),
// and the response `provider` is a runtime cross-check that the build agrees
// with the server's LIVE_TRANSLATION_PROVIDER.
export type CreateLiveTranslationSessionResponse =
    OpenAiLiveTranslationSessionResponse | GeminiLiveTranslationSessionResponse;

interface LiveTranslationSessionResponseBase {
    sessionId: number;
    provider: "openai" | "gemini";
    targetLanguage: string;
    expiresAt: string | null;
    model: string;
}

export interface OpenAiLiveTranslationSessionResponse extends LiveTranslationSessionResponseBase {
    provider: "openai";
    clientSecret: string;
    callsUrl: string;
    /** Bidirectional system prompt to inject via session.update on the
     *  data channel once the WebRTC connection is open. */
    instructions: string;
}

/** One fixed-target Gemini session the mobile must open. `outputRole` names
 *  who hears its audio: "nurse" outputs English (patient → nurse), "patient"
 *  outputs the patient language (nurse → patient). */
export interface GeminiLiveTranslationDirection {
    outputRole: "nurse" | "patient";
    targetLanguageCode: string;
}

/** Interpreter-mode setup the mobile sends verbatim on WS open (built + matched
 *  to the token's mint-time constraint server-side). Mirrors the backend
 *  GeminiInterpreterSetup. */
export interface GeminiInterpreterSetup {
    model: string;
    generationConfig: {
        responseModalities: string[];
        speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: string } } };
    };
    systemInstruction: { parts: { text: string }[] };
    realtimeInputConfig: {
        automaticActivityDetection: {
            startOfSpeechSensitivity: string;
            endOfSpeechSensitivity: string;
            prefixPaddingMs: number;
            silenceDurationMs: number;
        };
    };
    inputAudioTranscription: Record<string, never>;
    outputAudioTranscription: Record<string, never>;
}

export interface GeminiLiveTranslationSessionResponse extends LiveTranslationSessionResponseBase {
    provider: "gemini";
    /** Ephemeral token — passed as the `access_token` query param on wsUrl. */
    token: string;
    wsUrl: string;
    /** Interpreter mode: one session, send `setup` verbatim on WS open. */
    setup: GeminiInterpreterSetup;
    /** Legacy translate two-session transport; empty in interpreter mode. */
    directions: GeminiLiveTranslationDirection[];
    inputSampleRate: number;
    outputSampleRate: number;
}

export interface LiveTranslationTurnInput {
    role: "patient" | "nurse" | "system";
    text: string;
    startedAt: string;
    endedAt?: string | null;
}

export function createLiveTranslationSession(
    visitId: string,
    targetLanguage: string,
    provider?: "openai" | "gemini"
): Promise<CreateLiveTranslationSessionResponse> {
    return request(`/visits/${visitId}/live-translation/sessions`, {
        method: "POST",
        body: JSON.stringify({ targetLanguage, ...(provider ? { provider } : {}) })
    });
}

export function appendLiveTranslationTurns(
    visitId: string,
    sessionId: number,
    turns: LiveTranslationTurnInput[]
): Promise<{ appended: number; sessionExpired: boolean }> {
    return request(`/visits/${visitId}/live-translation/sessions/${sessionId}/turns`, {
        method: "POST",
        body: JSON.stringify({ turns })
    });
}

export function endLiveTranslationSession(
    visitId: string,
    sessionId: number,
    body: { status: "completed" | "failed"; endedAt: string; errorMessage?: string | null }
): Promise<{ sessionId: number; status: "completed" | "failed" }> {
    return request(`/visits/${visitId}/live-translation/sessions/${sessionId}`, {
        method: "PATCH",
        body: JSON.stringify(body)
    });
}

// --- Patient context (what is known about the patient before the visit) ---

export type ContextSourceType = "questionnaire" | "document" | "previous_visit" | "photo";

export interface ContextTimelineEntry {
    /** YYYY-MM-DD, or YYYY-MM / YYYY at lower precision — never a made-up day. */
    eventAt: string;
    precision: "day" | "month" | "year";
    eventType: string;
    text: string;
    itemIds: number[];
    sourceLabel: string;
}

/**
 * One answer option a source speaks to: affirmed (`present: true` — "cane
 * checked on the DME list") or explicitly denied (`present: false` —
 * "wheelchair not checked"). Options the source is silent about are absent.
 */
export interface ContextHintOption {
    /** Model evidence explanation; absent on older builds. */
    reasoning?: string;
    value: string;
    label: string;
    present: boolean;
    /** Bare: "the plan of care leaves the wheelchair box unticked". */
    clause: string;
    /** Attributed, ready to show: "CMS-485 plan of care notes the wheelchair box is unticked". */
    text: string;
    /** Another source says the opposite about this option — shown from the start, not on selection. */
    conflict?: { sourceLabel: string; present: boolean; clause: string };
}

export interface ContextHint {
    /** Null explicitly denotes relevant context without an answer. */
    answer?: string | null;
    evidenceQuotes?: string[];
    /** Model evidence explanation; absent on older builds. */
    reasoning?: string;
    sourceType?: "questionnaire" | "previous_visit" | "document" | "photo";
    sourceQuestionIds?: number[];
    sourceViewEnabled?: boolean;
    questionId: number;
    sectionId: number | null;
    /** A report, never an answer: "Daughter reports 2 falls, one with bruising." */
    text: string;
    sourceLabel: string;
    observedAt: string | null;
    itemIds: number[];
    imageRef: string | null;
    /** Per-answer messages: affirmed ones show from the start, a denied one only once that answer is selected — unless it conflicts. */
    options: ContextHintOption[];
    /** Set on a hint that exists to state a disagreement between two sources. */
    conflict?: { sources: string[]; values: string[] };
}

export interface ContextCheck {
    kind: "contradiction" | "omission" | "change";
    sectionId: number | null;
    questionId: number;
    text: string;
    itemIds: number[];
    /** Stored alongside so the app can re-evaluate against the current answer without the server. */
    origin: "mapped" | "model";
    sourceType: ContextSourceType;
    sourceLabel: string;
    observedAt: string | null;
    clause: string;
    /** What the source says per answer option; empty = the check cannot compare. */
    expects: ContextHintOption[];
    /** Checks frozen before `expects`: agreeing values, read as affirmed. */
    agreeValues?: string[];
}

export interface ContextItemView {
    canOpenSource?: boolean;
    id: number;
    kind: "intro" | "section_summary" | "fact" | "event";
    content: string;
    sectionId: number | null;
    questionId: number | null;
    sourceId: number;
    sourceType: ContextSourceType;
    sourceLabel: string;
    observedAt: string;
    fillerRole: string | null;
    imageRef: string | null;
    eventAt: string | null;
    eventPrecision: "day" | "month" | "year" | null;
    eventType: string | null;
}

export interface VisitContext {
    visitId: number;
    /**
     * The latest hints build: `none` = never built; `pending`/`processing` =
     * a rebuild is on its way (the hints shown are the previous ones); `ready`;
     * `failed` (the hints shown are the last good ones, if any).
     */
    status: "none" | "pending" | "processing" | "ready" | "failed";
    /** When the hints shown were built. */
    builtAt: string | null;
    error: string | null;
    /** The patient's context changed since the hints were built; a rebuild is queued. */
    stale: boolean;
    intro: string | null;
    timeline: ContextTimelineEntry[];
    hints: ContextHint[];
    checks: ContextCheck[];
    sources: Array<{ id: number; type: ContextSourceType; label: string; observedAt: string }>;
    sections: Array<{
        sectionId: number | null;
        sectionName: string | null;
        items: ContextItemView[];
    }>;
}

export function fetchVisitContext(visitId: string | number): Promise<VisitContext> {
    return request(`/visits/${visitId}/context`);
}

export function fetchContextItemImageUrl(
    visitId: string | number,
    itemId: number
): Promise<{ url: string }> {
    return request(`/visits/${visitId}/context/items/${itemId}/image`);
}

export interface ContextSourceEvidence {
    title?: string;
    cacheKey?: string;
    kind: "questions" | "document" | "text";
    text?: string;
    questions: Array<{
        id: number;
        question: string;
        answer: string;
        options: Array<{ value: string; text: string; state: string }>;
        parentIds: number[];
    }>;
    url: string | null;
    contentType: string | null;
    fileName: string | null;
}
export function fetchContextSourceEvidence(
    visitId: string | number,
    itemId: number,
    questionId?: number
) {
    return request<ContextSourceEvidence>(
        `/visits/${visitId}/context/items/${itemId}/evidence${questionId ? `?questionId=${questionId}` : ""}`
    );
}

export interface VisitDocument {
    index: number;
    title: string;
    cacheKey: string;
    fileName: string;
    contentType: string;
}
export function fetchVisitDocuments(
    visitId: string | number
): Promise<{ documents: VisitDocument[] }> {
    return request(`/visits/${visitId}/documents`);
}
export function fetchVisitDocumentUrl(
    visitId: string | number,
    index: number
): Promise<{
    url: string;
    fileName: string;
    contentType: string;
    title: string;
    cacheKey: string;
}> {
    return request(`/visits/${visitId}/documents/${index}`);
}
